import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { setAuthToken, clearAuthToken, getAuthToken } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/api/auth/me');
      if (data.role === 'superadmin' && !window.location.pathname.startsWith('/admin-gc2026')) {
        clearAuthToken();
        setUser(null);
        setLoading(false);
        return;
      }
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Seed _memoryToken from any available storage before the first API call,
    // so the Authorization header is present even on the /api/auth/me request.
    getAuthToken();
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/api/auth/login', { email, password });
    const token = data.access_token || data.token;
    if (token) setAuthToken(token);
    await checkAuth();
    return data;
  };

  const register = async (formData) => {
    const { data } = await api.post('/api/auth/register', formData);
    const token = data.access_token || data.token;
    if (token) setAuthToken(token);
    await checkAuth();
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch { /* ignore */ }
    clearAuthToken();
    setUser(false);
  };

  const userRole = user?.team_owner_id ? (user?.role || 'lecteur') : 'admin';

  return (
    <AuthContext.Provider value={{ user, userRole, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
